/**
 * Einkaufen: Warengruppen, Zusammenfassen gleicher Zutaten über Einheiten
 * hinweg, Abzug von Vorräten und die Frage, wie viele Packungen es braucht.
 *
 * Alles ist rein und ohne Datenbank, damit Server und Oberfläche dieselben
 * Regeln anwenden und sie sich testen lassen.
 */
import { densityFor, unitKind } from './conversion.ts';
import { parseQuantity, scaleQuantity } from './quantity.ts';
import type { ScalingMode } from './types.ts';
import { normalizeUnit } from './units.ts';

/** Warengruppen in der üblichen Reihenfolge eines Supermarkts. */
export const SHOPPING_CATEGORIES = [
  { id: 'produce', label: 'Obst & Gemüse' },
  { id: 'bakery', label: 'Brot & Backwaren' },
  { id: 'dairy', label: 'Milchprodukte & Eier' },
  { id: 'meat', label: 'Fleisch & Fisch' },
  { id: 'frozen', label: 'Tiefkühl' },
  { id: 'baking', label: 'Backen & Mehl' },
  { id: 'pantry', label: 'Nudeln, Reis & Konserven' },
  { id: 'spices', label: 'Gewürze, Öl & Soßen' },
  { id: 'sweets', label: 'Süßes & Snacks' },
  { id: 'drinks', label: 'Getränke' },
  { id: 'household', label: 'Drogerie & Haushalt' },
  { id: 'other', label: 'Sonstiges' },
] as const;

/** Kennung einer Warengruppe. */
export type ShoppingCategory = (typeof SHOPPING_CATEGORIES)[number]['id'];

/** Alle Kennungen, zum Prüfen von Eingaben. */
export const SHOPPING_CATEGORY_IDS: readonly ShoppingCategory[] = SHOPPING_CATEGORIES.map(
  (entry) => entry.id,
);

/** Bezeichnung einer Warengruppe. */
export function categoryLabel(id: string): string {
  return SHOPPING_CATEGORIES.find((entry) => entry.id === id)?.label ?? 'Sonstiges';
}

/**
 * Stichwörter je Warengruppe. Die Reihenfolge zählt: Genauere Angaben stehen
 * vorn, damit „Paprikapulver“ bei den Gewürzen landet und nicht beim Gemüse,
 * „Kokosmilch“ bei den Konserven und nicht bei der Milch.
 */
const CATEGORY_RULES: readonly (readonly [ShoppingCategory, RegExp])[] = [
  [
    'household',
    /spülmittel|toilettenpapier|küchenrolle|alufolie|backpapier|frischhaltefolie|müllbeutel|waschmittel|zahnpasta|shampoo|seife|schwamm/,
  ],
  ['frozen', /tiefkühl|\btk\b|tk-|gefrier|eiswürfel/],
  [
    'spices',
    /pulver\b|paprikapulver|gewürz|salz|pfeffer|zimt|curry|muskat|oregano|thymian getrocknet|kümmel|kreuzkümmel|nelke|kardamom|lorbeer|vanilleschote|öl\b|olivenöl|rapsöl|sonnenblumenöl|essig|senf|ketchup|mayonnaise|sojasauce|sojasoße|worcester|tabasco|brühe|fond|honig|sirup|tomatenmark/,
  ],
  [
    'baking',
    /mehl|zucker|backpulver|natron|hefe|stärke|kakao|kuvertüre|gelatine|mandel|haselnuss|walnuss|nüsse|nuss\b|haferflocken|rosinen|grieß|kokosraspel|vanille|zuckerguss|streusel|backoblaten/,
  ],
  [
    'pantry',
    /kokosmilch|passiert|stückige tomaten|dosentomaten|in der dose|dose|konserve|nudel|spaghetti|penne|pasta|lasagne|reis|risotto|couscous|bulgur|quinoa|linsen|kichererbsen|kidneybohnen|weiße bohnen|mais|polenta|erdnussbutter|marmelade|nutella|oliven|kapern|getrocknete tomaten/,
  ],
  ['sweets', /schokolade|kekse|gummibär|chips|bonbon|praline|knabber/],
  [
    'dairy',
    /milch|sahne|butter|joghurt|quark|käse|mozzarella|parmesan|feta|ricotta|mascarpone|schmand|crème|creme|frischkäse|(^|\s)eier?(\s|$)|eigelb|eiweiß|eiweiss|buttermilch|kefir/,
  ],
  [
    'meat',
    /fleisch|hack|rind|schwein|kalb|lamm|hähnchen|huhn|hühner|pute|ente|speck|schinken|wurst|salami|chorizo|lachs|fisch|forelle|kabeljau|thunfisch|garnele|shrimps|filet|steak|schnitzel|gulasch/,
  ],
  ['bakery', /brot|brötchen|toast|baguette|tortilla|wraps|ciabatta|croissant|blätterteig|pizzateig/],
  ['drinks', /wein|bier|saft|mineralwasser|sprudel|limonade|kaffee|tee\b|espresso/],
  [
    'produce',
    /apfel|äpfel|birne|banane|zitrone|limette|orange|mandarine|grapefruit|beere|erdbeer|himbeer|heidelbeer|kirsche|pflaume|pfirsich|aprikose|traube|melone|mango|ananas|kiwi|avocado|tomate|gurke|paprika|zwiebel|schalotte|knoblauch|kartoffel|süßkartoffel|möhre|karotte|salat|rucola|spinat|mangold|zucchini|aubergine|brokkoli|blumenkohl|kohl|wirsing|lauch|porree|sellerie|fenchel|pilz|champignon|kürbis|radieschen|rote bete|spargel|bohnen|erbsen|ingwer|chili|petersilie|schnittlauch|basilikum|dill|koriander|minze|rosmarin|thymian|salbei|kräuter|lauchzwiebel|frühlingszwiebel/,
  ],
];

/** Errät die Warengruppe aus dem Namen einer Zutat. */
export function guessCategory(name: string): ShoppingCategory {
  const lower = name.toLowerCase();
  for (const [category, pattern] of CATEGORY_RULES) {
    if (pattern.test(lower)) {
      return category;
    }
  }
  return 'other';
}

/** Was praktisch immer im Haus ist und nicht auf die Liste gehört. */
const ALWAYS_AT_HOME =
  /^(wasser|leitungswasser|warmes wasser|kaltes wasser|lauwarmes wasser|heißes wasser|eiswasser)$/;

/** Gehört die Zutat normalerweise nicht auf den Einkaufszettel? */
export function usuallyAtHome(name: string): boolean {
  return ALWAYS_AT_HOME.test(name.trim().toLowerCase());
}

/** Sonderfälle der Mehrzahl, die sich nicht über die Endung lösen lassen. */
const PLURALS: Readonly<Record<string, string>> = {
  eier: 'ei',
  äpfel: 'apfel',
  nüsse: 'nuss',
  walnüsse: 'walnuss',
  haselnüsse: 'haselnuss',
};

/** Grobe Grundform eines Wortes: „zwiebeln“ → „zwiebel“, „tomaten“ → „tomate“. */
function stem(word: string): string {
  const special = PLURALS[word];
  if (special !== undefined) {
    return special;
  }
  if (word.length > 4 && word.endsWith('en')) {
    return word.slice(0, -1);
  }
  if (word.length > 4 && word.endsWith('eln')) {
    return word.slice(0, -1);
  }
  return word;
}

/**
 * Vergleichsschlüssel einer Zutat. Gleiche Zutaten aus verschiedenen Rezepten
 * („Zwiebeln“, „Zwiebel“) bekommen denselben Schlüssel.
 */
export function shoppingKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9äöü]+/g, ' ')
    .trim()
    .split(' ')
    .filter((word) => word !== '')
    .map(stem)
    .join(' ');
}

/** Eine Menge mit Einheit; ohne Menge („etwas Salz“) ist amount null. */
export interface Measure {
  readonly amount: number | null;
  readonly unit: string;
}

/** Gramm je Gewichtseinheit und Milliliter je Volumeneinheit. */
const MASS_GRAMS: Readonly<Record<string, number>> = { mg: 0.001, g: 1, kg: 1000 };
const VOLUME_ML: Readonly<Record<string, number>> = {
  ml: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  TL: 5,
  EL: 15,
  Tasse: 250,
};

/** Rechnet eine Menge in Gramm oder Milliliter um; null bei Stück, Bund usw. */
function toBase(value: number, unit: string): { value: number; kind: 'mass' | 'volume' } | null {
  const normalized = normalizeUnit(unit);
  const grams = MASS_GRAMS[normalized.toLowerCase()];
  if (grams !== undefined) {
    return { value: value * grams, kind: 'mass' };
  }
  const millilitres = VOLUME_ML[normalized] ?? VOLUME_ML[normalized.toLowerCase()];
  if (millilitres !== undefined) {
    return { value: value * millilitres, kind: 'volume' };
  }
  return null;
}

/**
 * Fasst zwei Mengen derselben Zutat zusammen. Gewicht wird in Gramm, Volumen
 * in Millilitern addiert; Gewicht und Volumen lassen sich über die Dichte
 * verbinden. Liefert null, wenn die Einheiten nicht zusammenpassen
 * (zum Beispiel „1 Bund“ und „20 g“); dann bleiben es zwei Einträge.
 */
export function combineMeasures(existing: Measure, added: Measure, name: string): Measure | null {
  if (added.amount === null) {
    return existing;
  }
  if (existing.amount === null) {
    return added;
  }
  const unitA = normalizeUnit(existing.unit);
  const unitB = normalizeUnit(added.unit);
  if (unitA.toLowerCase() === unitB.toLowerCase()) {
    return { amount: existing.amount + added.amount, unit: unitA };
  }
  const a = toBase(existing.amount, unitA);
  const b = toBase(added.amount, unitB);
  if (a === null || b === null) {
    return null;
  }
  if (a.kind === b.kind) {
    return { amount: a.value + b.value, unit: a.kind === 'mass' ? 'g' : 'ml' };
  }
  const density = densityFor(name);
  if (density === null) {
    return null;
  }
  // In die Art der vorhandenen Menge umrechnen.
  const addedInKind = a.kind === 'mass' ? b.value * density : b.value / density;
  return { amount: a.value + addedInKind, unit: a.kind === 'mass' ? 'g' : 'ml' };
}

/** Ergebnis beim Abzug eines Vorrats. */
export interface PantryResult {
  /** Was noch gekauft werden muss; null, wenn der Vorrat reicht. */
  readonly remaining: Measure | null;
  /** Wurde überhaupt etwas abgezogen? */
  readonly used: boolean;
}

/**
 * Zieht einen Vorrat vom Bedarf ab. Ein Vorrat ohne Menge („Salz ist da“)
 * deckt den Bedarf ganz. Passen die Einheiten nicht, bleibt der Bedarf.
 */
export function subtractPantry(need: Measure, stock: Measure, name: string): PantryResult {
  if (stock.amount === null) {
    return { remaining: null, used: true };
  }
  if (need.amount === null) {
    return { remaining: null, used: true };
  }
  const negative: Measure = { amount: -stock.amount, unit: stock.unit };
  const combined = combineMeasures(need, negative, name);
  if (combined === null || combined.amount === null) {
    return { remaining: need, used: false };
  }
  // Kleine Reste (unter 2 %) zählen als gedeckt.
  const threshold = Math.abs(need.amount) * 0.02;
  if (combined.amount <= threshold) {
    return { remaining: null, used: true };
  }
  return { remaining: combined, used: true };
}

/** Packung eines Produkts. */
export interface PackageInfo {
  readonly amount: number;
  readonly unit: string;
  readonly priceCents?: number | null;
}

/**
 * Rechnet eine Menge in eine andere Einheit um (Gewicht, Volumen, über die
 * Dichte auch dazwischen). null, wenn das nicht geht.
 */
export function amountIn(value: number, from: string, to: string, name: string): number | null {
  if (normalizeUnit(from).toLowerCase() === normalizeUnit(to).toLowerCase()) {
    return value;
  }
  const source = toBase(value, from);
  const target = toBase(1, to);
  if (source === null || target === null) {
    return null;
  }
  if (source.kind === target.kind) {
    return source.value / target.value;
  }
  const density = densityFor(name);
  if (density === null) {
    return null;
  }
  const converted = source.kind === 'mass' ? source.value / density : source.value * density;
  return converted / target.value;
}

/** Wie viele Packungen werden gebraucht, und was kosten sie? */
export function packagesNeeded(
  need: Measure,
  pack: PackageInfo,
  name: string,
): { count: number; priceCents: number | null } | null {
  if (need.amount === null || pack.amount <= 0) {
    return null;
  }
  const needed = amountIn(need.amount, need.unit, pack.unit, name);
  if (needed === null) {
    return null;
  }
  // Etwas Spielraum, damit 1001 g nicht zwei Kilopackungen ergeben.
  const count = Math.max(1, Math.ceil(needed / pack.amount - 0.02));
  const price = pack.priceCents ?? null;
  return { count, priceCents: price === null ? null : count * price };
}

/** Eine Zutat, wie sie aus einem Rezept auf die Liste kommt. */
export interface ShoppingNeed {
  readonly key: string;
  readonly name: string;
  readonly measure: Measure;
  readonly category: ShoppingCategory;
  readonly note: string;
  /** Kennung des Produkts, falls die Zutat verknüpft ist. */
  readonly productId: string | null;
}

/** Was von einer Zutat gebraucht wird. */
export interface ShoppableIngredient {
  readonly amount: string;
  readonly unit: string;
  readonly name: string;
  readonly note?: string | null;
  readonly scaling: ScalingMode;
  readonly productId?: string | null;
}

/**
 * Bedarf eines Rezepts für die gewählte Menge. Bei Spannen („2–3 EL“) gilt
 * der obere Wert, damit es sicher reicht. Gleiche Zutaten innerhalb des
 * Rezepts werden zusammengefasst.
 */
export function recipeNeeds(ingredients: readonly ShoppableIngredient[], factor: number): ShoppingNeed[] {
  const needs: ShoppingNeed[] = [];
  for (const ingredient of ingredients) {
    const name = ingredient.name.trim();
    if (name === '') {
      continue;
    }
    const quantity = parseQuantity(ingredient.amount);
    const scaled = quantity === null ? null : scaleQuantity(quantity, factor, ingredient.scaling);
    const measure: Measure = {
      amount: scaled === null ? null : (scaled.max ?? scaled.value),
      unit: normalizeUnit(ingredient.unit),
    };
    const key = ingredient.productId ?? shoppingKey(name);
    const existing = needs.findIndex((need) => need.key === key);
    const combined =
      existing < 0 ? null : combineMeasures((needs[existing] as ShoppingNeed).measure, measure, name);
    if (existing >= 0 && combined !== null) {
      needs[existing] = { ...(needs[existing] as ShoppingNeed), measure: combined };
      continue;
    }
    needs.push({
      key,
      name,
      measure,
      category: guessCategory(name),
      note: ingredient.note ?? '',
      productId: ingredient.productId ?? null,
    });
  }
  return needs;
}

/** Ist die Einheit eine Gewichts- oder Volumeneinheit? Für die Anzeige von Packungen. */
export function isMeasurable(unit: string): boolean {
  return unitKind(unit) !== 'other';
}
